/**
 * Arena Panel - The Intelligent Standard
 *
 * Display panel for spreadsheet submissions in the arena.
 * Shows spreadsheet or JSON view with download options.
 */

import JsonView from '@uiw/react-json-view';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { downloadSheetSpecAsExcelSpreadJS } from '@/lib/utils/spreadjs-excel-export';

import { DownloadDropdown } from './DownloadDropdown';
import {
  ExpandButton,
  PanelEmptyState,
  PanelErrorState,
  ViewModeToggle,
} from './panel';
import { SpreadJSViewer } from './SpreadJSViewer';

interface ArenaPanelProps {
  label: string;
  submission: any;
  scrollId?: string;
  /** When true, removes outer container styling (for embedded use) */
  embedded?: boolean;
  /** Callback to render toolbar controls externally (e.g., in a parent header).
   *  When provided, toolbar won't be rendered inside the panel. */
  renderToolbar?: (controls: React.ReactNode) => void;
  /** When true, hides the spreadsheet/JSON toggle (defaults to false) */
  hideViewModeToggle?: boolean;
  /** When true, hides the download button */
  hideDownloadButton?: boolean;
}

interface ExpandedArenaPanelProps {
  label: string;
  submission: any;
  onClose: () => void;
}

export function ArenaPanel({
  label,
  submission,
  scrollId,
  embedded = false,
  renderToolbar,
  hideViewModeToggle = false,
  hideDownloadButton = false,
}: ArenaPanelProps) {
  const [viewMode, setViewMode] = useState<'spreadsheet' | 'json'>(
    'spreadsheet'
  );
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract data from submission (may be undefined)
  const sheetJson = submission?.sheet_json;
  const computedValues = submission?.compute_values;
  const engineChecks = submission?.engine_checks;

  // All hooks must be called before any conditional returns
  const handleDownloadExcel = useCallback(async () => {
    if (!sheetJson) return;
    try {
      const filename = `${label.toLowerCase().replace(/\s+/g, '-')}.xlsx`;
      await downloadSheetSpecAsExcelSpreadJS(sheetJson, filename);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to download Excel: ${message}`);
    }
  }, [sheetJson, label]);

  const handleDownloadJson = useCallback(() => {
    if (!sheetJson) return;
    const blob = new Blob([JSON.stringify(sheetJson, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${label.toLowerCase().replace(/\s+/g, '-')}.json`;
    a.click();
  }, [sheetJson, label]);

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleViewModeChange = useCallback((mode: string) => {
    setViewMode(mode as 'spreadsheet' | 'json');
  }, []);

  // Memoize toolbar controls to avoid recreating JSX on every render
  const toolbarControls = useMemo(
    () => (
      <>
        {!hideViewModeToggle && (
          <ViewModeToggle
            currentMode={viewMode}
            modes={[
              { label: 'Spreadsheet', value: 'spreadsheet' },
              { label: 'JSON', value: 'json' },
            ]}
            onChange={handleViewModeChange}
          />
        )}
        {!hideDownloadButton && (
          <DownloadDropdown
            onDownloadExcel={handleDownloadExcel}
            onDownloadJson={handleDownloadJson}
          />
        )}
        <ExpandButton onClick={handleExpand} />
      </>
    ),
    [
      viewMode,
      hideViewModeToggle,
      hideDownloadButton,
      handleViewModeChange,
      handleDownloadExcel,
      handleDownloadJson,
      handleExpand,
    ]
  );

  // Call renderToolbar callback to allow parent to render controls externally
  useEffect(() => {
    if (renderToolbar) {
      renderToolbar(toolbarControls);
    }
  }, [renderToolbar, toolbarControls]);

  // Document icon used in multiple empty states
  const documentIcon = (
    <svg className="w-6 h-6" fill="none" stroke="#A0A0A0" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );

  // Empty state - no submission
  if (!submission) {
    return (
      <PanelEmptyState
        icon={documentIcon}
        title={label}
        description="No submission data"
      />
    );
  }

  // Error state - with timeout detection
  if (submission.error) {
    return (
      <PanelErrorState
        label={label}
        error={submission.error}
        executionTimeMs={submission.execution_time_ms}
      />
    );
  }

  // No sheet data
  if (!sheetJson) {
    return (
      <div
        className="h-full rounded-xl flex flex-col"
        style={{
          background: '#FFFFFF',
          border: '1px solid #E8E0D8',
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid #E8E0D8' }}
        >
          <h3 className="text-sm font-medium" style={{ color: '#1A1A1A' }}>
            {label}
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <PanelEmptyState
            icon={documentIcon}
            title="No Spreadsheet Data"
            description="This result does not have valid data"
            embedded={true}
          />
        </div>
      </div>
    );
  }

  const containerStyle = embedded
    ? {}
    : {
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      };

  return (
    <>
      <div
        className={`h-full flex flex-col overflow-hidden ${embedded ? '' : 'rounded-xl'}`}
        style={containerStyle}
      >
        <div className="flex-1 min-h-0 p-2 flex flex-col">
          {/* Toolbar row - only render internally if no external renderToolbar */}
          {!renderToolbar && (
            <div className="flex gap-2 mb-2 items-center justify-end flex-shrink-0">
              {toolbarControls}
            </div>
          )}

          {viewMode === 'json' ? (
            <div
              className="flex-1 overflow-auto p-2 rounded-lg"
              style={{ background: '#FAFAF8', border: '1px solid #D1C9C4' }}
            >
              <JsonView
                value={sheetJson}
                collapsed={false}
                displayDataTypes={false}
                enableClipboard={true}
                style={{
                  fontSize: '0.75rem',
                  fontFamily: 'ui-monospace, monospace',
                  backgroundColor: 'transparent',
                }}
              />
            </div>
          ) : (
            <div className="flex-1 min-h-0">
              <SpreadJSViewer
                sheetJson={sheetJson}
                computedValues={computedValues}
                scrollId={scrollId}
                engineChecks={engineChecks}
                showExpandButton={false}
              />
            </div>
          )}
        </div>
      </div>

      {/* Expanded Modal - rendered via portal to document.body */}
      {isExpanded &&
        createPortal(
          <ExpandedArenaPanel
            label={label}
            submission={submission}
            onClose={() => setIsExpanded(false)}
          />,
          document.body
        )}
    </>
  );
}

function ExpandedArenaPanel({
  label,
  submission,
  onClose,
}: ExpandedArenaPanelProps) {
  const sheetJson = submission.sheet_json;
  const computedValues = submission.compute_values;
  const engineChecks = submission.engine_checks;

  // Add escape key handler to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(26, 26, 26, 0.75)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          width: '90vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.16)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header with Meridian styling */}
        <div
          style={{
            padding: '1.5rem 2rem',
            borderBottom: '1px solid #E8E0D8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#F5F0EB',
          }}
        >
          <div>
            <h3
              className="font-serif"
              style={{
                margin: 0,
                color: '#1A1A1A',
                fontSize: '1.75rem',
                fontWeight: 400,
                letterSpacing: '0em',
                lineHeight: 1.25,
              }}
            >
              {label || 'Model Response'}
            </h3>
            <p
              style={{
                margin: '0.25rem 0 0 0',
                color: '#6B6B6B',
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '0.01em',
              }}
            >
              Expanded View
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid #E8E0D8',
              borderRadius: '8px',
              width: '2.5rem',
              height: '2.5rem',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: '#6B6B6B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#E8E0D8';
              e.currentTarget.style.borderColor = '#D1C9C4';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = '#E8E0D8';
            }}
            title="Close (Esc)"
          >
            ×
          </button>
        </div>

        {/* Modal content - create new SpreadJS instance for expanded view */}
        <div style={{ flex: 1, overflow: 'hidden', padding: '1.5rem' }}>
          <SpreadJSViewer
            sheetJson={sheetJson}
            computedValues={computedValues}
            showFormulaBar={true}
            engineChecks={engineChecks}
            showExpandButton={false}
          />
        </div>
      </div>
    </div>
  );
}
